import React, { useState, useEffect } from 'react';
import ModalImage from 'react-modal-image';
import GpsUnlockButton from '../gpsUnlockButton/GPSUnlockButton';
import QrScanUnlock from '../qrScanUnlock/QRScanUnlock';
import RiddleUnlock from '../riddleUnlock/RiddleUnlock';
import CluePin from '../cluePin/CluePin';
import RewardCard from '../rewardCard/RewardCard';

const clueTypeIcon = {
  location: '📍',
  qr: '🔳',
  riddle: '🧩',
  trivia: '❓',
  default: '🕵️‍♂️',
};

const clueTypeColor = {
  location: '#2563eb',
  qr: '#10b981',
  riddle: '#f59e42',
  trivia: '#e11d48',
  default: '#334155',
};

function getDistanceMeters(lat1, lon1, lat2, lon2) {
  // Haversine formula
  const R = 6371000; // meters
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const ClueCard = ({
  clue,
  huntName,
  onClose,
  unlockedClues,
  fetchUnlockedClues,
}) => {
  // console.log('Unlocked Clues:', unlockedClues, 'Clue ID:', clue._id);
  const icon = clueTypeIcon[clue.type] || clueTypeIcon.default;
  const color = clueTypeColor[clue.type] || clueTypeColor.default;
  const [showReward, setShowReward] = useState(false);

  const [playerCoords, setPlayerCoords] = useState(null);
  const [distance, setDistance] = useState(null);

  // If not, adjust according to your backend
  // Get array of IDs for easier comparison
  const unlockedIds = unlockedClues.map((c) => c.clue);
  // Check if this clue is unlocked for the current player
  const isSolved = unlockedIds.includes(clue._id);
  // console.log(
  //   'Unlocked Clues:',
  //   unlockedClues,
  //   'Clue ID:',
  //   clue._id,
  //   'Is Solved:',
  //   isSolved,
  // );
  useEffect(() => {
    if (fetchUnlockedClues) {
      fetchUnlockedClues();
    }
    // Optionally, add clue._id to dependencies if you want to refetch when clue changes
  }, [clue._id]);

  useEffect(() => {
    if (isSolved) {
      setShowReward(true);
      if (navigator.vibrate) navigator.vibrate([50, 30, 50]);
    }
  }, [isSolved]);

  useEffect(() => {
    if (clue.type === 'location' && clue.location) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          };
          setPlayerCoords(coords);

          const clueLat = clue.location.coordinates[1];
          const clueLon = clue.location.coordinates[0];
          const dist = getDistanceMeters(
            coords.latitude,
            coords.longitude,
            clueLat,
            clueLon,
          );
          setDistance(dist);
        },
        () => setPlayerCoords(null),
        { enableHighAccuracy: true, timeout: 10000 },
      );
    }
  }, [clue]);

  // Example reward data, replace with actual reward logic if available
  const rewardData = clue.reward || {
    xp: clue.xp || 25,
    jgk: clue.jgk || 5,
    badge: clue.badge || '',
    message: clue.rewardMessage || '',
    streak: clue.streak || undefined,
  };

  return (
    <div
      className="card text-dark bg-light shadow-lg animate__animated animate__fadeIn"
      style={{
        borderRadius: 16,
        padding: '0.75rem 0.75rem',
        maxWidth: 440,
        border: `2.5px solid ${color}`,
        boxShadow: `0 8px 32px 0 ${color}22`,
        position: 'relative',
        overflow: 'hidden',
        margin: '0px auto',
      }}
    >
      <div className="card-body position-relative">
        {/* World-class fun pin at the top center */}
        <div
          style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}
        >
          <CluePin solved={isSolved} />
        </div>
        {/* Fun floating icon */}
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: -18,
            fontSize: 54,
            opacity: 0.18,
            pointerEvents: 'none',
            zIndex: 0,
            filter: 'blur(0.5px)',
          }}
        >
          {icon}
        </div>
        <h5
          className="card-title mb-2 text-center fw-bold"
          style={{
            fontSize: '1.35rem',
            color,
            letterSpacing: 1,
            textShadow: `0 2px 8px ${color}22`,
          }}
        >
          {huntName || 'Clue'}
        </h5>
        <div className="mb-2 text-center">
          <span
            className="badge"
            style={{
              background: color,
              color: '#fff',
              fontSize: 16,
              padding: '8px 18px',
              borderRadius: 12,
              boxShadow: `0 2px 8px ${color}22`,
              letterSpacing: 0.5,
            }}
          >
            {icon} {clue.type?.toUpperCase() || 'CLUE'}
          </span>
          {isSolved && (
            <span
              className="badge ms-2"
              style={{
                background: '#e5e7eb',
                color: '#ef4444',
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 10,
                marginLeft: 8,
                boxShadow: '0 2px 8px #ef444422',
                letterSpacing: 0.5,
                animation: 'pop 0.7s',
              }}
            >
              <span role="img" aria-label="check">
                ✔
              </span>{' '}
              Solved!
            </span>
          )}
        </div>
        {(clue.type === 'qr' || clue.type === 'location') &&
          clue.business?.name && (
            <div className="mb-2 text-center">
              <span
                className="badge"
                style={{
                  background: '#f59e42',
                  color: '#fff',
                  fontSize: 15,
                  padding: '7px 16px',
                  borderRadius: 10,
                  boxShadow: '0 2px 8px #f59e4222',
                  letterSpacing: 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  animation: 'pulse 1.2s infinite alternate',
                  maxWidth: 220,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  textAlign: 'center',
                }}
              >
                🏪{' '}
                <strong
                  style={{
                    fontWeight: 600,
                    display: 'inline-block',
                    width: '100%',
                    wordBreak: 'break-word',
                  }}
                >
                  {clue.business.name}
                </strong>
              </span>
              <style>
                {`
        @keyframes pulse {
          0% { transform: scale(1); box-shadow: 0 2px 8px #f59e4222; }
          100% { transform: scale(1.07); box-shadow: 0 4px 16px #f59e4244; }
        }
        @keyframes pop {
          0% { transform: scale(0.7); opacity: 0; }
          60% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}
              </style>
            </div>
          )}
        <p
          className="card-text mb-2 text-center"
          style={{
            fontSize: '1.15rem',
            fontWeight: 500,
            color: '#334155',
            background: '#f3f4f6',
            borderRadius: 10,
            padding: '10px 12px',
            boxShadow: '0 2px 8px #0001',
            marginBottom: clue.imageUrl ? 0 : 16,
          }}
        >
          {clue.text}
        </p>
        {/* Inline RewardCard animation after unlock */}
        {showReward && (
          <div className="mt-3 mb-2">
            <RewardCard
              title="🎉 Reward Unlocked!"
              rewards={rewardData}
              showConfetti={true}
              category={clue.type}
            />
          </div>
        )}
        {clue.imageUrl && (
          <div className="mb-3 text-center">
            <ModalImage
              small={clue.imageUrl}
              large={clue.imageUrl}
              alt="Clue Visual"
              className="img-thumbnail"
              style={{
                borderRadius: 14,
                maxWidth: 180,
                boxShadow: '0 4px 16px #0002',
                border: `2px solid ${color}`,
              }}
            />
          </div>
        )}
        {clue.type === 'location' && clue.location && (
          <div className="mb-2 text-center small text-muted">
            <div>
              <strong>Clue Location:</strong>{' '}
              {clue.location.coordinates[1].toFixed(5)},{' '}
              {clue.location.coordinates[0].toFixed(5)}
            </div>
            {playerCoords && (
              <div>
                <strong>Your Location:</strong>{' '}
                {playerCoords.latitude.toFixed(5)},{' '}
                {playerCoords.longitude.toFixed(5)}
              </div>
            )}
            {distance !== null && (
              <div>
                <strong>Distance:</strong> {distance.toFixed(1)} meters
              </div>
            )}
          </div>
        )}
        {/* Unlock buttons with animation */}
        <div className="mt-3 text-center">
          {clue.type === 'location' && clue.location && (
            <GpsUnlockButton
              clueId={clue._id}
              clueLocation={clue.location.coordinates}
              radiusMeters={clue.location.radiusMeters || 100}
              disabled={isSolved}
              unlockedClues={unlockedIds}
            />
          )}
          {clue.type === 'image' && clue.location?.coordinates && (
            <GpsUnlockButton
              clueId={clue._id}
              clueLocation={clue.location.coordinates}
              radiusMeters={clue.location.radiusMeters || 50}
              disabled={isSolved}
              unlockedClues={unlockedIds}
            />
          )}
          {clue.type === 'qr' && (
            <QrScanUnlock
              clueId={clue._id}
              disabled={isSolved}
              unlockedClues={unlockedIds}
            />
          )}
          {clue.type === 'riddle' && (
            <RiddleUnlock
              clueId={clue._id}
              clueText={clue.text}
              hint={clue.hint}
              disabled={isSolved}
              unlockedClues={unlockedIds}
            />
          )}
        </div>

        {/* Fun footer */}
        <div
          className="mt-4 d-flex justify-content-between align-items-center"
          style={{ fontSize: 14, color: '#64748b', opacity: 0.85 }}
        >
          <span>
            {isSolved
              ? '🎉 This clue is solved!'
              : clue.type === 'location'
              ? 'Unlock by visiting the location!'
              : clue.type === 'qr'
              ? 'Scan the QR code to unlock!'
              : 'Solve the clue to continue!'}
          </span>
          {onClose && (
            <button
              className="btn btn-sm btn-outline-secondary px-3"
              onClick={onClose}
              style={{
                borderRadius: 8,
                fontWeight: 500,
                boxShadow: '0 2px 8px #0001',
              }}
            >
              ✖ Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClueCard;
